import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// Material
import { makeStyles, Grid, Box, useTheme, useMediaQuery, Slide } from "@material-ui/core";

// components
import Button from "@/components/shared/Button";
import HeadingSection from "./headingSection";
import SearchField from "./searchField";

// actions
import { getStudentDetails } from "@/store/actions/studentDetailsActions";
import { showToasterMessage } from "@/store/actions/commonActions";
import { goToRoute } from "@/store/actions/navigationActions";
// utils
import { toasterMessage as TM } from "@/utils/toaster-msg";
import routePaths from "@/utils/route-paths";

const useStyles = makeStyles((theme) => ({
	studentSearchContainer: {
		marginTop: theme.spacing(8),
		position: "relative",
		width: "100%",
		background: theme.palette.primary.main,
		padding: theme.spacing(0, 2),
		height: theme.spacing(25.75),
		[theme.breakpoints.down("sm")]: {
			minHeight: theme.spacing(90),
			marginTop: theme.spacing(0),
			height: "100vh",
		},
	},
	fadedSvgContainer: {
		position: "absolute",
		top: 0,
		right: -0,
		[theme.breakpoints.down("sm")]: {
			top: "unset",
			bottom: -0,
		},
	},
	searchContainer: {
		padding: theme.spacing(0, 2),
	},
	searchInputBoxContainer: {
		width: "100%",
	},
	searchInputLabel: {
		color: theme.palette.common.white,
	},
	searchButtonContainer: {
		marginTop: theme.spacing(3.5),
		[theme.breakpoints.down("sm")]: {
			marginTop: theme.spacing(2),
		},
	},
	rightDivider: {
		borderRight: "1px solid white",
	},
	textWhite: {
		color: theme.palette.common.white,
		zIndex: 1,
		[theme.breakpoints.down("sm")]: {
			marginTop: theme.spacing(3),
		},
	},
	raisedContainer: {
		zIndex: 1,
	},
	mobileSearchBoxContainer: {
		marginTop: theme.spacing(5),
	},
	commonText: {
		margin: theme.spacing(3.25, 0),
		color: theme.palette.common.white,
		zIndex: 1,
	},
	QRCode: {
		zIndex: 1,
	}
}));

export default function StudentSearch({ studentSearchProps, studentId }) {
    const classes = useStyles();
	const theme = useTheme();
	const dispatch = useDispatch();
    const [registrantCode, setRegistrantCode] = useState(studentId ? studentId.split(",").slice(0, 5) : []);
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isTablet = useMediaQuery(theme.breakpoints.down("md"));
	const universityDetails = useSelector((state) => state.universityDetails.data);
	const location = useSelector((state) => state.router.location);
	useEffect(() => {
		let search = new URLSearchParams(location.search);
		const code = search.get("registrant_code");
		if (code && universityDetails.universityCode) {
			const payload = {
				registrant_code: code.trim(),
				university_code: universityDetails.universityCode,
				fair_id: universityDetails.fairID,
                counsellor_access: universityDetails.showPU,
			};
            dispatch(getStudentDetails(payload));
            setRegistrantCode(code.split(",").slice(0, 5));
		} else if (!code) {
			// Cleared after successful move (or Clear All + navigation)
			setRegistrantCode([]);
		}
	}, [location]);

	const commonActions = useSelector((state) => state.common);
	const handleStudentSearch = (registrantCode) => {
		if (registrantCode.length) {
			dispatch(
				goToRoute(routePaths.DASHBOARD, null, {
					registrant_code: registrantCode,
				})
			);
		} else {
			dispatch(showToasterMessage(TM.incorrectUniqueId.msg, TM.incorrectUniqueId.color));
		}
	};

    const handleSearchBoxChange = (_, value) => {
        setRegistrantCode(value);
	};

	if (isMobile) {
		return (
			<Slide
				timeout={300}
				direction="right"
				in={!commonActions.showStudentDetailsInMobile}
				mountOnEnter
				unmountOnExit
			>
				<Box className={classes.studentSearchContainer}>
					<Box className={classes.fadedSvgContainer}>
						<img src={studentSearchProps.azentFadedSvg} />
					</Box>
					<HeadingSection headingProps={studentSearchProps.headingSection} />
					<Grid
						className={classes.mobileSearchBoxContainer}
						container
						alignItems="center"
						justifyContent="center"
					>
						<Grid item xs={12}>
							<Box>
                                <SearchField
                                    {...studentSearchProps.inputBox}
                                    onChange={handleSearchBoxChange}
                                    value={registrantCode}
                                />
							</Box>
						</Grid>
						<Grid item xs={12}>
							<Box className={classes.searchButtonContainer}>
								<Button
									{...studentSearchProps.searchButton}
									fullWidth={true}
									onClick={() => {
										handleStudentSearch(registrantCode);
									}}
								/>
							</Box>
						</Grid>
					</Grid>						
				</Box>
			</Slide>
		);
	}
	return (
		<Box className={classes.studentSearchContainer}>
			<Box className={classes.fadedSvgContainer}>
				<img src={studentSearchProps.azentFadedSvg} />
			</Box>
			<HeadingSection headingProps={studentSearchProps.headingSection} />
            <Box mt={3} display="flex" alignItems="center" justifyContent="center">
                <Grid
                    container
                    alignItems="flex-end"
                    justifyContent={isTablet ? "center" : "flex-end"}
                    spacing={2}
                >
                    <Grid item xs={8} md={9}>
                        <SearchField
                            {...studentSearchProps.inputBox}
                            onChange={handleSearchBoxChange}
                            value={registrantCode}
                        />
                    </Grid>
                    <Grid item xs={4} md={2}>
                        <Button
                            {...studentSearchProps.searchButton}
                            onClick={() => handleStudentSearch(registrantCode)}
                        />
                    </Grid>
                </Grid>
            </Box>
		</Box>
	);
}

StudentSearch.propTypes = {
	studentId: PropTypes.string,
	studentSearchProps: PropTypes.shape({
		azentFadedSvg: PropTypes.any.isRequired,
		QRCode: PropTypes.any.isRequired,
		headingSection: PropTypes.shape({
			title: PropTypes.string.isRequired,
			subTitle: PropTypes.string.isRequired,
		}).isRequired,
		inputBox: PropTypes.shape({
			label: PropTypes.string.isRequired,
		}).isRequired,
		searchButton: PropTypes.shape({
			label: PropTypes.string.isRequired,
			variant: PropTypes.string.isRequired,
			color: PropTypes.string.isRequired,
		}).isRequired,
	}),
};
